import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import newWithConfirmationModal from 'Components/reusable/HOC/newWithConfirmationModal'
import { useAllocations } from 'hooks/allocations'
import { add } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IEmployee } from 'types/adminInterfaces'
import { IAllocation } from 'types/assignmentInterfaces'
import { IError } from 'types/error'
import { chooseDBTranslation } from 'utils/translations'
import { convertToDisplayDate } from 'utils/utils'
import CaleoDatePicker from 'Components/reusable/InputFields/CaleoDatePicker'
import CloseIcon from '@mui/icons-material/Close'
import DateRangeIcon from '@mui/icons-material/DateRange'

/**
 * @notExported
 */
interface IDeleteEmployeeConfirmProps {
  /** Employee being deleted */
  item: IEmployee
  /** If the employee is being deleted from the network management system */
  networkManage?: boolean
  /** Function for setting the delete date */
  setDeleteDate: (date: Date | null) => void
  /** Data for the employee */
  data: Date | null
}

/**
 * Component for confirming employee deletion
 *
 * @returns Confirmation modal for employee deletion
 * @notExported
 */
const DeleteEmployeeConfirm: React.FC<IDeleteEmployeeConfirmProps> = ({ item, networkManage, setDeleteDate, data }) => {
  const { t, i18n } = useTranslation()
  const { allocations, error } = useAllocations(item.Person.id)
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [backendError, setBackendError] = useState<IError>()

  useEffect(() => {
    if (error) setBackendError(error as IError)
  }, [error])

  /**
   * Get name or type of allocation.
   *
   * @param allocation - Current allocation
   * @returns Name of assignment or type of allocation
   * @notExported
   */
  const getName = (allocation: IAllocation) => {
    if (allocation.type === 'project') {
      if (allocation.Assignment && allocation.Assignment.translations) {
        return chooseDBTranslation(i18n, allocation.Assignment).name
      }
      return t('allocation.project')
    } else if (allocation.type === 'personal') {
      return t('allocation.ownProject')
    } else {
      return t('allocation.other')
    }
  }

  const handleChange = (date: Date | null) => {
    setDeleteDate(date)
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent>
      <Grid container size={12} spacing={2}>
        <Grid size={12}>
          <Typography color="primary" fontWeight="bold" variant="h2" textTransform="none">
            {t('confirmation.user.data', { name: `${item.Person.firstName} ${item.Person.lastName}` })}
          </Typography>
        </Grid>
        <Grid size={12} container spacing={2}>
          <Typography>{t('confirmation.text')}</Typography>
          {networkManage && (
            <Grid>
              <Typography>{t('confirmation.networkManage')}</Typography>
              <Typography>{t('confirmation.networkManage.confirm')}</Typography>
            </Grid>
          )}
          {allocations.length > 0 ? (
            <Grid>
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="checkbox-admin-employee-delete-allocation-collapse"
                    onChange={() => setDetailsOpen(!detailsOpen)}
                    icon={<ExpandMoreIcon />}
                    checkedIcon={<ExpandLessIcon />}
                    name="collapseCh"
                  />
                }
                label={t('allocation')}
              />
              <Collapse in={detailsOpen}>
                <Table aria-label="allocation table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('allocation.project')}</TableCell>
                      <TableCell align="right">{t('allocation.startDate')}</TableCell>
                      <TableCell align="right">{t('allocation.endDate')}</TableCell>
                      <TableCell align="right">{t('allocation.information')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocations.map((allocation: IAllocation) => (
                      <TableRow key={allocation.id}>
                        <TableCell variant="head" scope="row">
                          {getName(allocation)}
                        </TableCell>
                        <TableCell align="right">{convertToDisplayDate(allocation.startDate)}</TableCell>
                        <TableCell align="right">{convertToDisplayDate(allocation.endDate)}</TableCell>
                        <TableCell align="right">{chooseDBTranslation(i18n, allocation).information}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Collapse>
            </Grid>
          ) : null}
          <Typography>
            {t('no-undo')} {t('confirmation.user.text')}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography>{t('confirmation.user.setDeleteDateInfo')}</Typography>
          <CaleoDatePicker
            id="deleteDate"
            selected={data}
            onChange={handleChange}
            dateFormat={i18n.language === 'fi' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
            minDate={add(new Date(), { days: 1 })}
            customInput={
              <TextField
                data-testid="field-admin-employee-delete-date"
                margin="dense"
                label={t('confirmation.user.setDeleteDate')}
                variant="outlined"
                size="small"
                required={false}
                disabled={false}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {data && (
                          <IconButton
                            data-testid="button-admin-employee-clear-delete-date"
                            onClick={() => setDeleteDate(null)}
                            onMouseDown={() => setDeleteDate(null)}
                            size="small"
                            sx={{ p: 0, m: 0 }}
                          >
                            <CloseIcon style={{ cursor: 'pointer' }} fontSize="small" />
                          </IconButton>
                        )}
                        <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                      </InputAdornment>
                    ),
                  },
                  inputLabel: { shrink: true },
                  formHelperText: { style: { textAlign: 'right' } },
                }}
              />
            }
            popperPlacement="bottom-start"
            showMonthDropdown
            useShortMonthInDropdown
            showYearDropdown
          />
        </Grid>
      </Grid>
    </DialogContent>
  )
}

export default newWithConfirmationModal(DeleteEmployeeConfirm)
